import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  cancelAnimationFrame,
  requestAnimationFrame,
} from "@/utils/requestAnimationFrame";

type CounterProps = {
  duration: number;
  start: number;
  end: number;
  children: any;
};

type TweenProps = {
  from: number;
  to: number;
  duration: number;
  children: any;
  onComplete: () => void;
};

const easings = {
  cubic: (n: any) => --n * n * n + 1,
};

const TweenNumber: React.FC<TweenProps> = ({
  from,
  to,
  duration,
  children,
  onComplete,
}) => {
  const [value, setValue] = useState(to);
  const startTimeRef = useRef<number | null>(null);
  const stopRef = useRef(false);
  const frameRef = useRef<number | null>(null);

  const draw = useCallback((): void => {
    if (!startTimeRef.current) return;

    const now = Date.now();
    if (now - startTimeRef.current >= duration) {
      stopRef.current = true;
      setValue(to);
      onComplete();
      return;
    }

    let percentage = (now - startTimeRef.current) / duration;
    percentage = percentage > 1 ? 1 : percentage;
    const easeVal = easings.cubic(percentage);
    const val = from + (to - from) * easeVal;

    setValue(Math.round(val));
  }, [from, to, duration, onComplete]);

  const animate = useCallback((): void => {
    if (stopRef.current) {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      return;
    }
    frameRef.current = requestAnimationFrame(animate);
    draw();
  }, [draw]);

  useEffect(() => {
    startTimeRef.current = Date.now();
    stopRef.current = false;
    frameRef.current = requestAnimationFrame(animate);

    return () => {
      stopRef.current = true;
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [animate]);

  return children(value);
};

const Counter: React.FC<CounterProps> = ({
  start,
  end,
  duration,
  children,
}) => {
  const [isTweening, setIsTweening] = useState(true);

  const handleComplete = useCallback(() => {
    setIsTweening(false);
  }, []);

  if (isTweening) {
    return (
      <TweenNumber
        from={start}
        to={end}
        duration={duration}
        onComplete={handleComplete}
      >
        {(tween: any) => children(tween)}
      </TweenNumber>
    );
  }

  return <React.Fragment>{children(end)}</React.Fragment>;
};

export default Counter;
